import React from 'react'
import {ArrowLeft, Shield, Eye, Lock, Database, Cookie} from 'lucide-react'

interface PrivacyPageProps {
  onBack: () => void
}

export function PrivacyPage({ onBack }: PrivacyPageProps) {
  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0A0E27] via-[#0d1339] to-[#0A0E27] pb-16">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Back Button */}
        <button
          onClick={onBack}
          className="flex items-center gap-2 px-4 py-2 bg-white/5 text-white rounded-lg hover:bg-white/10 transition-colors mb-8"
        >
          <ArrowLeft className="w-5 h-5" />
          Back to Home
        </button>

        {/* Header */}
        <div className="mb-12">
          <h1 className="text-5xl font-bold text-white mb-4">Privacy Policy</h1>
          <p className="text-gray-400">Last updated: January 2025</p>
        </div>

        {/* Content */}
        <div className="space-y-8">
          {/* Introduction */}
          <section className="bg-white/5 border border-white/10 rounded-xl p-6">
            <div className="flex items-start gap-4">
              <Shield className="w-6 h-6 text-[#60A5FA] flex-shrink-0 mt-1" />
              <div>
                <h2 className="text-2xl font-bold text-white mb-3">Our Commitment to Privacy</h2>
                <p className="text-gray-300 mb-3">
                  At Base Builders, we take your privacy seriously. This Privacy Policy explains how we collect, use, protect, and handle your personal information when you use our platform.
                </p>
                <p className="text-gray-300">
                  By using Base Builders, you agree to the collection and use of information in accordance with this policy.
                </p>
              </div>
            </div>
          </section>

          {/* Information We Collect */}
          <section className="bg-white/5 border border-white/10 rounded-xl p-6">
            <div className="flex items-start gap-4">
              <Database className="w-6 h-6 text-[#60A5FA] flex-shrink-0 mt-1" />
              <div>
                <h2 className="text-2xl font-bold text-white mb-3">Information We Collect</h2>
                
                <h3 className="text-xl font-bold text-white mb-2 mt-4">Account Information</h3>
                <ul className="space-y-2 text-gray-300 mb-4">
                  <li className="flex items-start gap-2">
                    <span className="text-[#60A5FA] mt-1">•</span>
                    <span>Email address (for account authentication and communication)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#60A5FA] mt-1">•</span>
                    <span>Username and authentication credentials</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#60A5FA] mt-1">•</span>
                    <span>User ID and session data</span>
                  </li>
                </ul>

                <h3 className="text-xl font-bold text-white mb-2 mt-4">Project Information</h3>
                <ul className="space-y-2 text-gray-300 mb-4">
                  <li className="flex items-start gap-2">
                    <span className="text-[#60A5FA] mt-1">•</span>
                    <span>Project name, description, and profile details</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#60A5FA] mt-1">•</span>
                    <span>Banner images, profile pictures, and media gallery content</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#60A5FA] mt-1">•</span>
                    <span>Social media links and website URLs</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#60A5FA] mt-1">•</span>
                    <span>Contact information (email, Telegram)</span>
                  </li>
                </ul>

                <h3 className="text-xl font-bold text-white mb-2 mt-4">Payment Information</h3>
                <ul className="space-y-2 text-gray-300 mb-4">
                  <li className="flex items-start gap-2">
                    <span className="text-[#60A5FA] mt-1">•</span>
                    <span>Subscription status and payment history</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#60A5FA] mt-1">•</span>
                    <span>Stripe customer ID (payment processing is handled by Stripe)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#60A5FA] mt-1">•</span>
                    <span><strong className="text-white">Note:</strong> We do NOT store credit card information. All payment data is securely processed and stored by Stripe.</span>
                  </li>
                </ul>

                <h3 className="text-xl font-bold text-white mb-2 mt-4">Usage Analytics</h3>
                <ul className="space-y-2 text-gray-300">
                  <li className="flex items-start gap-2">
                    <span className="text-[#60A5FA] mt-1">•</span>
                    <span>Page views and click statistics</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#60A5FA] mt-1">•</span>
                    <span>Browser type and device information</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#60A5FA] mt-1">•</span>
                    <span>IP address and general location data</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#60A5FA] mt-1">•</span>
                    <span>Time and date of visits</span>
                  </li>
                </ul>
              </div>
            </div>
          </section>

          {/* How We Use Your Information */}
          <section className="bg-white/5 border border-white/10 rounded-xl p-6">
            <div className="flex items-start gap-4">
              <Eye className="w-6 h-6 text-[#60A5FA] flex-shrink-0 mt-1" />
              <div>
                <h2 className="text-2xl font-bold text-white mb-3">How We Use Your Information</h2>
                <ul className="space-y-2 text-gray-300">
                  <li className="flex items-start gap-2">
                    <span className="text-[#60A5FA] mt-1">•</span>
                    <span><strong className="text-white">Platform Operations:</strong> To provide, maintain, and improve our services</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#60A5FA] mt-1">•</span>
                    <span><strong className="text-white">Account Management:</strong> To authenticate users and manage subscriptions</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#60A5FA] mt-1">•</span>
                    <span><strong className="text-white">Project Display:</strong> To publicly display your project information as part of the platform listings</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#60A5FA] mt-1">•</span>
                    <span><strong className="text-white">Analytics:</strong> To provide you with performance metrics and insights</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#60A5FA] mt-1">•</span>
                    <span><strong className="text-white">Communication:</strong> To send important updates about your account or subscription</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#60A5FA] mt-1">•</span>
                    <span><strong className="text-white">Security:</strong> To detect and prevent fraud, abuse, and violations of our Terms</span>
                  </li>
                </ul>
              </div>
            </div>
          </section>

          {/* Information Sharing */}
          <section className="bg-white/5 border border-white/10 rounded-xl p-6">
            <div className="flex items-start gap-4">
              <Lock className="w-6 h-6 text-[#60A5FA] flex-shrink-0 mt-1" />
              <div>
                <h2 className="text-2xl font-bold text-white mb-3">Information Sharing & Disclosure</h2>
                <p className="text-gray-300 mb-3">
                  We do not sell, trade, or rent your personal information to third parties. We may share information in the following circumstances:
                </p>
                <ul className="space-y-2 text-gray-300">
                  <li className="flex items-start gap-2">
                    <span className="text-[#60A5FA] mt-1">•</span>
                    <span><strong className="text-white">Public Display:</strong> Project information you provide (name, description, images, links) is displayed publicly on the platform</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#60A5FA] mt-1">•</span>
                    <span><strong className="text-white">Service Providers:</strong> With Stripe for payment processing, and Lumi SDK for authentication and database services</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#60A5FA] mt-1">•</span>
                    <span><strong className="text-white">Legal Compliance:</strong> When required by law or to protect our rights and the safety of our users</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#60A5FA] mt-1">•</span>
                    <span><strong className="text-white">Business Transfers:</strong> In the event of a merger, acquisition, or sale of assets</span>
                  </li>
                </ul>
              </div>
            </div>
          </section>

          {/* Data Security */}
          <section className="bg-white/5 border border-white/10 rounded-xl p-6">
            <h2 className="text-2xl font-bold text-white mb-3">Data Security</h2>
            <p className="text-gray-300 mb-3">
              We implement industry-standard security measures to protect your information:
            </p>
            <ul className="space-y-2 text-gray-300">
              <li className="flex items-start gap-2">
                <span className="text-[#60A5FA] mt-1">•</span>
                <span>Secure authentication through Lumi SDK</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-[#60A5FA] mt-1">•</span>
                <span>Encrypted data transmission (HTTPS/SSL)</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-[#60A5FA] mt-1">•</span>
                <span>Regular security audits and updates</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-[#60A5FA] mt-1">•</span>
                <span>Secure payment processing through Stripe (PCI-DSS compliant)</span>
              </li>
            </ul>
            <p className="text-gray-300 mt-3">
              However, no method of transmission over the internet is 100% secure. While we strive to protect your information, we cannot guarantee absolute security.
            </p>
          </section>

          {/* Cookies */}
          <section className="bg-white/5 border border-white/10 rounded-xl p-6">
            <div className="flex items-start gap-4">
              <Cookie className="w-6 h-6 text-[#60A5FA] flex-shrink-0 mt-1" />
              <div>
                <h2 className="text-2xl font-bold text-white mb-3">Cookies & Tracking</h2>
                <p className="text-gray-300 mb-3">
                  We use cookies and similar tracking technologies to improve your experience:
                </p>
                <ul className="space-y-2 text-gray-300">
                  <li className="flex items-start gap-2">
                    <span className="text-[#60A5FA] mt-1">•</span>
                    <span><strong className="text-white">Session cookies:</strong> To keep you logged in</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#60A5FA] mt-1">•</span>
                    <span><strong className="text-white">Analytics cookies:</strong> To understand how users interact with our platform</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#60A5FA] mt-1">•</span>
                    <span><strong className="text-white">Preference cookies:</strong> To remember your settings</span>
                  </li>
                </ul>
                <p className="text-gray-300 mt-3">
                  You can control cookies through your browser settings, but this may affect platform functionality.
                </p>
              </div>
            </div>
          </section>

          {/* Your Rights */}
          <section className="bg-white/5 border border-white/10 rounded-xl p-6">
            <h2 className="text-2xl font-bold text-white mb-3">Your Privacy Rights</h2>
            <p className="text-gray-300 mb-3">You have the right to:</p>
            <ul className="space-y-2 text-gray-300">
              <li className="flex items-start gap-2">
                <span className="text-[#60A5FA] mt-1">•</span>
                <span>Access and review your personal information</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-[#60A5FA] mt-1">•</span>
                <span>Update or correct your information through your dashboard</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-[#60A5FA] mt-1">•</span>
                <span>Request deletion of your account and data</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-[#60A5FA] mt-1">•</span>
                <span>Opt-out of marketing communications</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-[#60A5FA] mt-1">•</span>
                <span>Export your data in a portable format</span>
              </li>
            </ul>
            <p className="text-gray-300 mt-3">
              To exercise these rights, please contact us via Telegram.
            </p>
          </section>

          {/* Third-Party Services */}
          <section className="bg-white/5 border border-white/10 rounded-xl p-6">
            <h2 className="text-2xl font-bold text-white mb-3">Third-Party Services</h2>
            <p className="text-gray-300 mb-3">
              Base Builders integrates with the following third-party services:
            </p>
            <ul className="space-y-2 text-gray-300">
              <li className="flex items-start gap-2">
                <span className="text-[#60A5FA] mt-1">•</span>
                <span><strong className="text-white">Stripe:</strong> Payment processing (subject to Stripe's Privacy Policy)</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-[#60A5FA] mt-1">•</span>
                <span><strong className="text-white">Lumi SDK:</strong> Authentication and database services</span>
              </li>
            </ul>
            <p className="text-gray-300 mt-3">
              These services have their own privacy policies, and we encourage you to review them.
            </p>
          </section>

          {/* Children's Privacy */}
          <section className="bg-white/5 border border-white/10 rounded-xl p-6">
            <h2 className="text-2xl font-bold text-white mb-3">Children's Privacy</h2>
            <p className="text-gray-300">
              Base Builders is not intended for users under the age of 18. We do not knowingly collect personal information from children. If you believe we have collected information from a child, please contact us immediately.
            </p>
          </section>

          {/* Changes to Policy */}
          <section className="bg-white/5 border border-white/10 rounded-xl p-6">
            <h2 className="text-2xl font-bold text-white mb-3">Changes to Privacy Policy</h2>
            <p className="text-gray-300">
              We may update this Privacy Policy from time to time. Changes will be posted on this page with an updated "Last updated" date. We encourage you to review this policy periodically.
            </p>
          </section>

          {/* Contact */}
          <section className="bg-white/5 border border-white/10 rounded-xl p-6">
            <h2 className="text-2xl font-bold text-white mb-3">Contact Us</h2>
            <p className="text-gray-300 mb-2">
              If you have questions about this Privacy Policy or your personal information, please contact us:
            </p>
            <a 
              href="https://t.me/chumponbase" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-[#60A5FA] hover:text-[#3B82F6] transition-colors"
            >
              Telegram: @chumponbase
            </a>
          </section>
        </div>
      </div>
    </div>
  )
}